import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import { brands, brandCampaigns } from "@/mocks/brandData";

const categoryFilters = [
  { id: "all", label: "전체" },
  { id: "beauty", label: "뷰티" },
  { id: "fashion", label: "패션" },
  { id: "tech", label: "테크" },
  { id: "lifestyle", label: "라이프스타일" },
  { id: "food", label: "푸드" },
  { id: "sports", label: "스포츠" },
];

export default function BrandsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredBrands = brands.filter((b) => {
    const matchCat = activeCategory === "all" || b.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.tagline.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  const totalTokenPool = brandCampaigns.reduce((sum, c) => sum + c.tokenPool, 0);
  const totalParticipants = brandCampaigns.reduce((sum, c) => sum + c.participantCount, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cfae500928337889ea29437188767775.jpg"
            alt="Brands Hero"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/60 via-transparent to-[#0a0a0f]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/80 via-transparent to-[#0a0a0f]/80" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            {/* Left: Title */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Link to="/" className="text-white/30 text-sm hover:text-white/60 transition-colors cursor-pointer">홈</Link>
                <i className="ri-arrow-right-s-line text-white/20 text-sm" />
                <span className="text-white/60 text-sm">브랜드 IP</span>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#f5e642]/20 to-[#ff8c42]/20 border border-[#f5e642]/30">
                  <i className="ri-building-2-line text-2xl text-[#f5e642]" />
                </div>
                <div>
                  <p className="text-[#f5e642] text-xs font-bold uppercase tracking-widest">Brand IP Marketplace</p>
                </div>
              </div>

              <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
                브랜드 IP 활용하고<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f5e642] to-[#ff8c42]">
                  토큰 보너스
                </span> 받기
              </h1>
              <p className="text-white/50 text-base leading-relaxed max-w-xl">
                브랜드사가 제공하는 제품 사진, 패션 아이템, 로고 등 공식 IP를 영상 프롬프트에 활용하면
                기본 보상에 추가 토큰을 받을 수 있어요. 브랜드와 크리에이터가 함께 성장하는 새로운 방식.
              </p>
            </div>

            {/* Right: Stats */}
            <div className="flex flex-col gap-3 w-full lg:w-auto">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: "참여 브랜드", value: `${brands.length}+`, icon: "ri-building-2-line", color: "#f5e642" },
                  { label: "활성 캠페인", value: `${brandCampaigns.filter(c => c.status === "active").length}`, icon: "ri-megaphone-line", color: "#ff8c42" },
                  { label: "총 토큰 풀", value: `${(totalTokenPool / 10000).toFixed(0)}만`, icon: "ri-coin-line", color: "#00f5d4" },
                  { label: "참여 크리에이터", value: `${(totalParticipants / 1000).toFixed(1)}K`, icon: "ri-group-line", color: "#e8a0b4" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl mx-auto mb-2" style={{ background: `${stat.color}20` }}>
                      <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                    </div>
                    <div className="font-black text-xl text-white">{stat.value}</div>
                    <div className="text-white/40 text-xs mt-0.5">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-6 mb-12">
        <div className="rounded-2xl border border-[#f5e642]/20 bg-[#f5e642]/5 p-6">
          <div className="flex items-center gap-2 mb-5">
            <i className="ri-question-line text-[#f5e642]" />
            <h2 className="text-white font-bold text-base">브랜드 IP 활용 방법</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {[
              { step: "01", icon: "ri-search-eye-line", title: "브랜드 IP 탐색", desc: "원하는 브랜드의 IP 에셋을 찾아보세요", color: "#f5e642" },
              { step: "02", icon: "ri-at-line", title: "IP 태그 삽입", desc: "프롬프트에 @브랜드_태그를 추가하세요", color: "#ff8c42" },
              { step: "03", icon: "ri-magic-line", title: "영상 생성", desc: "AI가 브랜드 IP를 반영한 영상을 만들어요", color: "#00f5d4" },
              { step: "04", icon: "ri-coin-fill", title: "토큰 보너스 수령", desc: "기본 보상 + 브랜드 IP 보너스 토큰 지급", color: "#e8a0b4" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${item.color}20`, border: `1px solid ${item.color}30` }}>
                  <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                </div>
                <div>
                  <p className="text-[10px] font-bold mb-0.5" style={{ color: item.color }}>STEP {item.step}</p>
                  <p className="text-white/80 text-xs font-semibold">{item.title}</p>
                  <p className="text-white/35 text-[11px] mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Brand Campaigns */}
      <section className="max-w-7xl mx-auto px-6 mb-12">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-bold text-lg">진행 중인 브랜드 캠페인</h2>
          <span className="text-white/30 text-xs">{brandCampaigns.filter(c => c.status === "active").length}개 활성</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {brandCampaigns.filter(c => c.status === "active").map((campaign) => {
            const brand = brands.find(b => b.id === campaign.brandId);
            if (!brand) return null;
            return (
              <div key={campaign.id} className="rounded-2xl border border-white/10 bg-white/3 overflow-hidden hover:border-white/20 transition-all group cursor-pointer">
                <div className="relative h-36 overflow-hidden">
                  <img src={campaign.thumbnail} alt={campaign.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/40 to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-green-500/20 text-green-400 border border-green-500/30">
                      진행 중
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-lg" style={{ background: `${brand.accentColor}25`, border: `1px solid ${brand.accentColor}40` }}>
                    <i className="ri-coin-fill text-xs" style={{ color: brand.accentColor }} />
                    <span className="text-xs font-bold" style={{ color: brand.accentColor }}>{campaign.tokenPerUse.toLocaleString()}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg overflow-hidden flex-shrink-0 border border-white/10">
                      <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs font-semibold" style={{ color: brand.accentColor }}>{brand.name}</span>
                  </div>
                  <h3 className="text-white font-bold text-sm mb-1.5">{campaign.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed mb-3 line-clamp-2">{campaign.description}</p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/30">{campaign.participantCount.toLocaleString()}명 참여</span>
                    <span className="text-white/30">~{campaign.deadline}</span>
                  </div>
                  <div className="mt-3 p-2.5 rounded-lg bg-white/5 border border-white/8">
                    <p className="text-[11px]" style={{ color: brand.accentColor }}>
                      <i className="ri-gift-line mr-1" />
                      {campaign.bonusCondition}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Search & Filter */}
      <section className="max-w-7xl mx-auto px-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="브랜드 검색..."
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/25 outline-none focus:border-white/25 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {categoryFilters.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
                style={
                  activeCategory === cat.id
                    ? { background: "#f5e642", color: "#0a0a0f" }
                    : { background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.45)", border: "1px solid rgba(255,255,255,0.08)" }
                }
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Brand List */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-bold text-lg">참여 브랜드</h2>
          <span className="text-white/30 text-xs">{filteredBrands.length}개 브랜드</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredBrands.map((brand) => (
            <Link key={brand.id} to={`/brands/${brand.id}`} className="block">
              <div className="rounded-2xl border border-white/10 bg-white/3 overflow-hidden hover:border-white/20 transition-all group cursor-pointer">
                {/* Cover */}
                <div className="relative h-40 overflow-hidden">
                  <img src={brand.coverImage} alt={brand.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/30 to-transparent" />
                  {/* Token reward badge */}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{ background: `${brand.accentColor}25`, border: `1px solid ${brand.accentColor}50` }}>
                    <i className="ri-coin-fill text-sm" style={{ color: brand.accentColor }} />
                    <span className="font-black text-sm" style={{ color: brand.accentColor }}>+{brand.tokenReward}</span>
                    <span className="text-white/40 text-xs">토큰/사용</span>
                  </div>
                  {brand.verified && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 border border-white/20">
                      <i className="ri-verified-badge-fill text-xs" style={{ color: brand.accentColor }} />
                      <span className="text-white/70 text-[10px] font-semibold">공식 브랜드</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-white/10">
                      <img src={brand.logo} alt={brand.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="text-white font-bold text-base">{brand.name}</h3>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${brand.accentColor}20`, color: brand.accentColor }}>
                          {brand.categoryLabel}
                        </span>
                      </div>
                      <p className="text-white/40 text-xs">{brand.tagline}</p>
                    </div>
                  </div>

                  {/* IP Assets Preview */}
                  <div className="mb-4">
                    <p className="text-white/30 text-[11px] font-semibold uppercase tracking-wider mb-2">제공 IP 에셋</p>
                    <div className="flex items-center gap-2">
                      {brand.ipAssets.slice(0, 3).map((asset) => (
                        <div key={asset.id} className="relative group/asset">
                          <div className="w-14 h-14 rounded-xl overflow-hidden border border-white/10">
                            <img src={asset.image} alt={asset.name} className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap opacity-0 group-hover/asset:opacity-100 transition-opacity"
                            style={{ background: brand.accentColor, color: "#0a0a0f" }}>
                            {asset.tag}
                          </div>
                        </div>
                      ))}
                      {brand.ipAssets.length > 3 && (
                        <div className="w-14 h-14 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center">
                          <span className="text-white/40 text-xs font-bold">+{brand.ipAssets.length - 3}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 pt-3 border-t border-white/8">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-image-line text-xs text-white/30" />
                      <span className="text-white/40 text-xs">{brand.ipAssets.length}개 IP</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-bar-chart-line text-xs text-white/30" />
                      <span className="text-white/40 text-xs">{brand.totalUsage.toLocaleString()}회 활용</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-flashlight-line text-xs" style={{ color: brand.accentColor }} />
                      <span className="text-xs font-semibold" style={{ color: brand.accentColor }}>x{brand.bonusMultiplier} 보너스</span>
                    </div>
                    <div className="ml-auto">
                      <span className="text-white/30 text-xs group-hover:text-white/60 transition-colors">IP 보기 →</span>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredBrands.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 mx-auto mb-4">
              <i className="ri-building-2-line text-2xl text-white/20" />
            </div>
            <p className="text-white/30 text-sm">검색 결과가 없습니다</p>
          </div>
        )}

        {/* Brand Registration CTA */}
        <div className="mt-12 rounded-2xl border border-[#f5e642]/20 bg-gradient-to-br from-[#f5e642]/8 to-[#ff8c42]/5 p-8 text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4" style={{ background: "rgba(245,230,66,0.15)", border: "1px solid rgba(245,230,66,0.3)" }}>
            <i className="ri-building-4-line text-2xl text-[#f5e642]" />
          </div>
          <h3 className="text-white font-black text-xl mb-2">브랜드사이신가요?</h3>
          <p className="text-white/50 text-sm leading-relaxed mb-6 max-w-md mx-auto">
            IP를 제공하고 수천 명의 크리에이터가 만드는 영상에 브랜드를 노출하세요.
            팬덤 기반의 강력한 바이럴 마케팅을 경험해보세요.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/brand-register"
              className="px-6 py-3 rounded-xl font-bold text-sm text-[#0a0a0f] cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #f5e642, #ff8c42)" }}
            >
              <i className="ri-add-line mr-2" />
              브랜드 IP 등록하기
            </Link>
            <button className="px-6 py-3 rounded-xl font-bold text-sm text-white/60 border border-white/15 hover:border-white/30 hover:text-white transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-file-text-line mr-2" />
              파트너십 문의
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
